import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { CACHE_KEYS } from "@rentablo/api/apiv1";
import { useFeatureFlag } from "@hooks/useFeatureFlag";
import { useClassifiedAccounts } from "./useClassifiedAccounts";
import { useDeleteAccounts } from "./useDeleteAccounts";
import { useBankImportParams } from "./useBankImportParams";

export const useAccountReview = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { bankConnectionId, flow, isEarlyRedirect, clearParams } =
    useBankImportParams();
  const isEnabled = useFeatureFlag("ACCOUNT_REVIEW");

  const { optionalAccounts, requiredRows, isLoading, isFetched } =
    useClassifiedAccounts(
      bankConnectionId,
      isEnabled && bankConnectionId != null && flow === "import"
    );

  const { deleteAccounts, isDeleting } = useDeleteAccounts();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const toggleAccount = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const confirm = useCallback(async () => {
    const idsToDelete = optionalAccounts
      .map((a) => a.id)
      .filter((id) => !selectedIds.has(id));

    if (idsToDelete.length === 0) {
      clearParams();
      return;
    }

    const results = await deleteAccounts(idsToDelete);
    await queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_ACCOUNTS],
    });

    const hasFailure = results.some((r) => r.status === "rejected");
    if (hasFailure) {
      enqueueSnackbar(t("accountReview.deleteError"), {
        variant: "error",
        autoHideDuration: 3000,
      });
      return;
    }

    clearParams();
  }, [
    optionalAccounts,
    selectedIds,
    clearParams,
    deleteAccounts,
    queryClient,
    enqueueSnackbar,
    t,
  ]);

  useEffect(() => {
    if (isFetched && optionalAccounts.length === 0) {
      clearParams();
    }
  }, [isFetched, optionalAccounts.length, clearParams]);

  const isOpen =
    isEnabled &&
    !!bankConnectionId &&
    !isEarlyRedirect &&
    !isLoading &&
    optionalAccounts.length > 0;

  return {
    isOpen,
    optionalAccounts,
    requiredRows,
    selectedIds,
    toggleAccount,
    confirm,
    close: confirm,
    isConfirming: isDeleting,
  };
};
